import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_API_URL } from '../helpers/Config';

const StakingScreen = ({ theme = 'dark' }) => {
    const isDarkMode = theme === 'dark';
    const navigate = useNavigate();

    const [statsData, setStatsData] = useState({
        balance: 0,
        staking_amount: 0,
        active_count: 0,
        total_paid: 0,
        total_pending: 0,
        total_return: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const themeColors = {
        dark: {
            background: '#0F172A',
            surface: '#1E293B',
            surfaceLight: '#2D3748',
            primary: '#8B5CF6',
            primaryHover: '#7C3AED',
            text: '#F1F5F9',
            textSecondary: '#94A3B8',
            border: '#334155',
            icon: '#94A3B8',
            shadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            success: '#10B981',
            warning: '#F59E0B',
            error: '#EF4444'
        },
        light: {
            background: '#F8FAFC',
            surface: '#FFFFFF',
            surfaceLight: '#F1F5F9',
            primary: '#7C3AED',
            primaryHover: '#6D28D9',
            text: '#1E293B',
            textSecondary: '#64748B',
            border: '#E2E8F0',
            icon: '#64748B',
            shadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            success: '#059669',
            warning: '#D97706',
            error: '#DC2626'
        }
    };

    const colors = isDarkMode ? themeColors.dark : themeColors.light;

    const menuItems = [
        {
            id: 'new-staking',
            title: 'New Recovery Trade',
            icon: '💰',
            description: 'Start new Recovery Trades',
            path: '/new-staking'
        },
        {
            id: 'my-staking',
            title: 'My Recovery Trades',
            icon: '📊',
            description: 'Manage active capitals',
            path: '/my-staking'
        },
        {
            id: 'commission-list',
            title: 'Commission',
            icon: '📃',
            description: 'Earnings history',
            path: '/staking-commision'
        },
        {
            id: 'support',
            title: 'Support',
            icon: '🆘',
            description: 'Help center',
            path: '/support'
        }
    ];

    const fetchStakingStats = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("a5srf4wer7w1cd");

            if (!token) {
                setError("Please login again");
                return;
            }

            const response = await axios.post(`${BASE_API_URL}/staking/stats`, {
                token: token,
            }, {
                headers: { 'Content-Type': 'application/json' }
            });

            const data = response?.data;
            if (data?.error === false && data?.data) {
                setStatsData(data.data);
                setError(null);
            } else {
                setError(data?.message || "Failed to fetch stats");
            }
        } catch (error) {
            setError(error.response?.data?.message || "Network error");
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(amount);
    };

    useEffect(() => {
        fetchStakingStats();
    }, []);

    const handleItemClick = (item) => {
        navigate(item.path);
    };

    const styles = `
        .staking-screen-container {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            max-width: 500px;
            margin: 0 auto;
            -webkit-tap-highlight-color: transparent;
        }
        
        .compact-card {
            border-radius: 16px;
            border: 1px solid ${colors.border};
            background: ${colors.surface};
            box-shadow: ${colors.shadow};
            margin-bottom: 12px;
            transition: all 0.2s ease;
        }
        
        .compact-card:hover {
            box-shadow: 0 4px 12px ${isDarkMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.08)'};
        }
        
        .menu-item-compact {
            border-radius: 14px;
            border: 1px solid ${colors.border};
            background: ${colors.surface};
            cursor: pointer;
            transition: all 0.2s ease;
            padding: 12px 16px;
            margin-bottom: 8px;
        }
        
        .menu-item-compact:hover {
            transform: translateY(-1px);
            border-color: ${colors.primary};
            box-shadow: 0 4px 12px ${colors.primary}15;
        }
        
        .menu-icon-small {
            width: 42px;
            height: 42px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            background: ${colors.primary}10;
            color: ${colors.primary};
            flex-shrink: 0;
        }
        
        .stat-badge {
            padding: 3px 10px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 600;
            background: ${colors.primary}15;
            color: ${colors.primary};
            white-space: nowrap;
        }
        
        .action-btn-small {
            padding: 10px 16px;
            border-radius: 12px;
            font-weight: 600;
            font-size: 14px;
            border: none;
            cursor: pointer;
            transition: all 0.15s ease;
            flex: 1;
        }
        
        .action-btn-small:active {
            transform: scale(0.98);
        }
        
        .stats-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
        }
        
        .stat-item-compact {
            padding: 12px;
            border-radius: 12px;
            background: ${colors.background};
            border: 1px solid ${colors.border};
        }
        
        .balance-large {
            font-size: 32px;
            font-weight: 700;
            color: ${colors.text};
            line-height: 1.1;
            letter-spacing: -0.5px;
        }
        
        .balance-subtext {
            font-size: 13px;
            color: ${colors.textSecondary};
            margin-top: 2px;
        }
        
        .skeleton-shimmer {
            background: linear-gradient(90deg, ${colors.border} 25%, ${colors.surfaceLight} 50%, ${colors.border} 75%);
            background-size: 200% 100%;
            animation: loading 1.5s infinite;
        }
        
        @keyframes loading {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
        }
        
        .refresh-btn-small {
            width: 36px;
            height: 36px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: ${colors.primary}10;
            color: ${colors.primary};
            border: 1px solid ${colors.border};
            cursor: pointer;
            transition: all 0.15s ease;
        }
        
        .refresh-btn-small:hover {
            background: ${colors.primary}15;
        }
        
        .refresh-btn-small:active {
            transform: scale(0.95);
        }
        
        .compact-section-title {
            font-size: 14px;
            font-weight: 600;
            color: ${colors.textSecondary};
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 12px;
            display: flex;
            align-items: center;
            gap: 8px;
        }
        
        .compact-section-title::after {
            content: '';
            flex: 1;
            height: 1px;
            background: ${colors.border};
        }
        
        .error-banner-compact {
            border-radius: 12px;
            padding: 12px;
            background: ${colors.error}10;
            border: 1px solid ${colors.error}20;
            margin-bottom: 12px;
            font-size: 13px;
        }
        
        .header-compact {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 16px;
        }
        
        @media (max-width: 768px) {
            .balance-large {
                font-size: 28px;
            }
            
            .stat-item-compact {
                padding: 10px;
            }
        }
    `;

    const SkeletonLoader = () => (
        <>
            {/* Header Skeleton */}
            <div className="header-compact">
                <div>
                    <div className="skeleton-shimmer" style={{ height: '28px', width: '120px', borderRadius: '8px', marginBottom: '6px' }}></div>
                    <div className="skeleton-shimmer" style={{ height: '16px', width: '180px', borderRadius: '6px' }}></div>
                </div>
                <div className="skeleton-shimmer refresh-btn-small"></div>
            </div>

            {/* Balance Card Skeleton */}
            <div className="compact-card p-3 mb-3">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div style={{ width: '100%' }}>
                        <div className="skeleton-shimmer mb-1" style={{ height: '14px', width: '40%', borderRadius: '6px' }}></div>
                        <div className="skeleton-shimmer" style={{ height: '32px', width: '60%', borderRadius: '8px' }}></div>
                    </div>
                </div>
                <div className="d-flex gap-2">
                    <div className="skeleton-shimmer action-btn-small" style={{ height: '40px' }}></div>
                    <div className="skeleton-shimmer action-btn-small" style={{ height: '40px' }}></div>
                </div>
            </div>

            {/* Stats Grid Skeleton */}
            <div className="stats-grid mb-3">
                {[1, 2, 3, 4].map((item) => (
                    <div key={item} className="stat-item-compact">
                        <div className="skeleton-shimmer mb-1" style={{ height: '12px', width: '60%', borderRadius: '6px' }}></div>
                        <div className="skeleton-shimmer" style={{ height: '20px', width: '80%', borderRadius: '6px' }}></div>
                    </div>
                ))}
            </div>

            {/* Menu Items Skeleton */}
            {[1, 2, 3, 4].map((item) => (
                <div key={item} className="menu-item-compact">
                    <div className="d-flex align-items-center">
                        <div className="skeleton-shimmer menu-icon-small me-3"></div>
                        <div style={{ flex: 1 }}>
                            <div className="skeleton-shimmer mb-1" style={{ height: '16px', width: '40%', borderRadius: '6px' }}></div>
                            <div className="skeleton-shimmer" style={{ height: '14px', width: '60%', borderRadius: '6px' }}></div>
                        </div>
                    </div>
                </div>
            ))}
        </>
    );

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="staking-screen-container"
            style={{
                minHeight: '100vh',
                padding: '16px',
                background: colors.background,
                color: colors.text,
                transition: 'background 0.2s ease, color 0.2s ease'
            }}
        >
            <style>{styles}</style>

            {error && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="error-banner-compact"
                >
                    <div className="d-flex align-items-center justify-content-between">
                        <span style={{ color: colors.error }}>{error}</span>
                        <button
                            onClick={fetchStakingStats}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: colors.primary,
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            Retry
                        </button>
                    </div>
                </motion.div>
            )}

            {loading ? (
                <SkeletonLoader />
            ) : (
                <>
                    {/* Compact Header */}
                    <div className="header-compact">
                        <div>
                            <h1 style={{
                                fontWeight: 700,
                                fontSize: '24px',
                                color: colors.text,
                                margin: 0,
                                lineHeight: 1.2
                            }}>
                                Recovery Trade
                            </h1>
                            <p style={{
                                fontSize: '13px',
                                color: colors.textSecondary,
                                margin: 0,
                                opacity: 0.8
                            }}>
                                Manage capital & rewards
                            </p>
                        </div>
                        <motion.button
                            whileTap={{ scale: 0.95, rotate: 180 }}
                            onClick={fetchStakingStats}
                            className="refresh-btn-small"
                            title="Refresh"
                        >
                            ↻
                        </motion.button>
                    </div>

                    {/* Combined Balance & Quick Actions */}
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        className="compact-card p-3 mb-3"
                    >
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div>
                                <div style={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: colors.textSecondary,
                                    marginBottom: '2px'
                                }}>
                                    WALLET BALANCE
                                </div>
                                <div className="balance-large">
                                    {formatCurrency(statsData.balance)}
                                </div>
                                <div className="balance-subtext">
                                    Available for Recovery Trade
                                </div>
                            </div>
                            <div className="stat-badge">
                                {statsData.active_count} Active
                            </div>
                        </div>

                        <div className="d-flex gap-2">
                            <motion.button
                                whileTap={{ scale: 0.98 }}
                                onClick={() => {
                                    navigate('../fund-deposit')
                                }}
                                className="action-btn-small"
                                style={{
                                    background: colors.primary,
                                    color: '#FFFFFF'
                                }}
                            >
                                Deposit
                            </motion.button>
                            <motion.button
                                whileTap={{ scale: 0.98 }}
                                onClick={() => {
                                    navigate("../withdrawl")
                                }}
                                className="action-btn-small"
                                style={{
                                    background: colors.surface,
                                    color: colors.text,
                                    border: `1px solid ${colors.border}`
                                }}
                            >
                                Withdraw
                            </motion.button>
                        </div>
                    </motion.div>

                    {/* Compact Stats Grid */}
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.15 }}
                        className="stats-grid mb-4"
                    >
                        <div className="stat-item-compact">
                            <div style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: colors.textSecondary,
                                marginBottom: '6px',
                                textTransform: 'uppercase'
                            }}>
                                Capital Amount
                            </div>
                            <div style={{
                                fontSize: '18px',
                                fontWeight: 700,
                                color: colors.text
                            }}>
                                {formatCurrency(statsData.staking_amount)}
                            </div>
                        </div>

                        <div className="stat-item-compact">
                            <div style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: colors.textSecondary,
                                marginBottom: '6px',
                                textTransform: 'uppercase'
                            }}>
                                Pending Rewards
                            </div>
                            <div style={{
                                fontSize: '18px',
                                fontWeight: 700,
                                color: colors.warning
                            }}>
                                {formatCurrency(statsData.total_pending)}
                            </div>
                        </div>

                        <div className="stat-item-compact">
                            <div style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: colors.textSecondary,
                                marginBottom: '6px',
                                textTransform: 'uppercase'
                            }}>
                                Total Paid
                            </div>
                            <div style={{
                                fontSize: '18px',
                                fontWeight: 700,
                                color: colors.success
                            }}>
                                {formatCurrency(statsData.total_paid)}
                            </div>
                        </div>

                        <div className="stat-item-compact">
                            <div style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: colors.textSecondary,
                                marginBottom: '6px',
                                textTransform: 'uppercase'
                            }}>
                                Total Recovery
                            </div>
                            <div style={{
                                fontSize: '18px',
                                fontWeight: 700,
                                color: colors.success
                            }}>
                                {formatCurrency(statsData.total_return)}
                            </div>
                        </div>
                    </motion.div>

                    {/* Section Title */}
                    <div className="compact-section-title">
                        Quick Actions
                    </div>

                    {/* Compact Menu Items */}
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                    >
                        {menuItems.map((item, index) => (
                            <motion.div
                                key={item.id}
                                whileTap={{ scale: 0.98 }}
                            >
                                <div
                                    className="menu-item-compact"
                                    onClick={() => handleItemClick(item)}
                                >
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center" style={{ flex: 1 }}>
                                            <div className="menu-icon-small me-3">
                                                {item.icon}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div className="d-flex align-items-center justify-content-between mb-1">
                                                    <h3 style={{
                                                        fontSize: '15px',
                                                        fontWeight: 600,
                                                        margin: 0,
                                                        color: colors.text,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                        {item.title}
                                                    </h3>
                                                    {item.badge && (
                                                        <span className="stat-badge">
                                                            {item.badge}
                                                        </span>
                                                    )}
                                                </div>
                                                <p style={{
                                                    fontSize: '12px',
                                                    margin: 0,
                                                    color: colors.textSecondary,
                                                    lineHeight: 1.4
                                                }}>
                                                    {item.description}
                                                </p>
                                            </div>
                                        </div>
                                        <div style={{
                                            color: colors.icon,
                                            marginLeft: '8px',
                                            fontSize: '16px',
                                            opacity: 0.7
                                        }}>
                                            →
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Compact Info Banner */}
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="compact-card p-3 mt-4"
                        style={{
                            background: `linear-gradient(135deg, ${colors.primary}10 0%, ${colors.primary}05 100%)`,
                            border: `1px solid ${colors.primary}20`
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '10px',
                                background: colors.primary + '20',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: colors.primary,
                                marginRight: '12px',
                                fontSize: '16px',
                                flexShrink: 0
                            }}>
                                ⚡
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: colors.text,
                                    marginBottom: '2px'
                                }}>
                                    Limited Time Offer
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    color: colors.textSecondary,
                                    lineHeight: 1.4
                                }}>
                                    30% bonus on Recovery Trade above $1000
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </motion.div>
    );
};

export default StakingScreen;